import { planLib, roomLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryStates } from "nuqs"
import { Suspense, useEffect, useMemo, useRef } from "react"
import { Book, Edit, FileText, Home, MoreHorizontal, Plus } from "react-feather"
import { useForm } from "react-hook-form"
import { Link, useLocation, useNavigate } from "react-router-dom"
import type { z as zod } from "zod"
import * as z from "zod"
import { type TenantModels, tenantApi } from "@/api/client/tenant"
import {
  type TenantList200TenantsItem,
  type TenantList200TenantsItemRoom,
  TenantListIsEchoesLinked,
} from "@/api/generated/models"
import { tenantListQueryParams } from "@/api/generated/schemas/api.zod"
import {
  EchoesSyncCell,
  echoesLinkedFilterOptions,
  echoesLinkedFilterValues,
  normalizeEchoesLinkedFilter,
} from "@/components/echoes/EchoesSyncCard"
import { Button } from "@/components/ui/button"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/DropdownMenu"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Select } from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner/Spinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { openInNewTab } from "@/lib/navigation"
import { cn } from "@/lib/utils"

const searchFormSchema = tenantListQueryParams
  .extend({
    id: z.number().optional(),
    title: z.string().optional(),
    propertyNameLike: z.string().optional(),
    roomNameLike: z.string().optional(),
    propertyAddressLike: z.string().optional(),
    nameKanaLike: z.string().optional(),
    phoneLike: z.string().optional(),
    startDateFrom: z.date().optional(),
    startDateTo: z.date().optional(),
    endDateFrom: z.date().optional(),
    endDateTo: z.date().optional(),
    isEchoesLinked: z.enum(echoesLinkedFilterValues).optional(),
  })
  .omit({
    page: true,
    limit: true,
  })
  .superRefine((values, context) => {
    if (values.startDateFrom && values.startDateTo && values.startDateFrom > values.startDateTo) {
      context.addIssue({
        code: "custom",
        message: "FromはTo以前の日付を選択してください",
        path: ["startDateTo"],
      })
    }
    if (values.endDateFrom && values.endDateTo && values.endDateFrom > values.endDateTo) {
      context.addIssue({
        code: "custom",
        message: "FromはTo以前の日付を選択してください",
        path: ["endDateTo"],
      })
    }
  })

type SearchFormValues = zod.infer<typeof searchFormSchema>

// nuqsのパーサー定義
const pageParsers = {
  page: parseAsInteger.withDefault(1),
  id: parseAsInteger,
  propertyNameLike: parseAsString,
  roomNameLike: parseAsString,
  propertyAddressLike: parseAsString,
  nameLike: parseAsString,
  nameKanaLike: parseAsString,
  phoneLike: parseAsString,
  withProperty: parseAsBoolean,
  startDateFrom: parseAsString,
  startDateTo: parseAsString,
  endDateFrom: parseAsString,
  endDateTo: parseAsString,
  isEchoesLinked: parseAsString,
}

const parseDate = (dateStr: string | null | undefined): Date | undefined => {
  if (!dateStr) return undefined
  const date = new Date(dateStr)
  return Number.isNaN(date.getTime()) ? undefined : date
}

// DateをAPIフォーマット（YYYY-MM-DD）に変換するヘルパー
const formatDateForApi = (date: Date | undefined): string | null => {
  if (!date) return null
  return format(date, "yyyy-MM-dd")
}

const createTicketSearchUrl = (params: {
  tenantName?: string | null
  propertyName?: string | null
  roomName?: string | null
}) => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value)
    }
  })
  const queryString = searchParams.toString()
  return queryString ? `/tickets?${queryString}` : "/tickets"
}

const getTenantRoomPlanInfo = (tenant: TenantList200TenantsItem) => {
  const room = tenant.room
  if (!room) {
    return { label: "未利用", variant: "nonePlan" as const, suspended: false }
  }

  const activePlan = tenant.plans?.find((plan) => planLib.isCurrent(plan, null))
  if (activePlan) {
    return roomLib.getRoomServicePlanInfo({ ...room, plans: [activePlan] })
  }

  const plannedPlan = (tenant.plans ?? [])
    .filter((plan) => planLib.isFuture(plan, null))
    .sort((firstPlan, secondPlan) => firstPlan.startDate.localeCompare(secondPlan.startDate))[0]
  if (!plannedPlan) {
    return { label: "未利用", variant: "nonePlan" as const, suspended: false }
  }

  const plannedPlanInfo = roomLib.getRoomServicePlanInfo({ ...room, plans: [plannedPlan] })
  return {
    ...plannedPlanInfo,
    label: `${plannedPlanInfo.label}利用開始前`,
  }
}

const formatOptionalPhone = (
  phone1: string | null | undefined,
  phone2: string | null | undefined,
  phone3: string | null | undefined
) => {
  const phoneParts = [phone1, phone2, phone3].map((phonePart) => phonePart?.trim() ?? "")
  if (phoneParts.every((phonePart) => phonePart.length === 0)) return null
  return formatPhone(phoneParts[0], phoneParts[1], phoneParts[2])
}

const getTenantPhones = (tenant: TenantList200TenantsItem) =>
  [
    formatOptionalPhone(tenant.phone1, tenant.phone2, tenant.phone3),
    formatOptionalPhone(tenant.phone4, tenant.phone5, tenant.phone6),
  ].filter((phone): phone is string => phone !== null)

export const TenantPage = () => {
  const itemsPerPage = 20
  const idInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  // URLクエリパラメーター管理
  const [queryParams, setQueryParams] = useQueryStates(pageParsers)

  // フォームの値をクエリから復元
  const searchParams = useMemo(() => {
    if (!queryParams) return null
    const hasSearchParams = Object.entries(queryParams)
      .filter(([key]) => key !== "page")
      .some(
        ([_, value]) =>
          value !== null && value !== undefined && (Array.isArray(value) ? value.length > 0 : true)
      )

    if (!hasSearchParams) return null

    return {
      page: queryParams.page,
      limit: itemsPerPage,
      id: queryParams.id || undefined,
      propertyNameLike: queryParams.propertyNameLike || undefined,
      roomNameLike: queryParams.roomNameLike || undefined,
      propertyAddressLike: queryParams.propertyAddressLike || undefined,
      nameLike: queryParams.nameLike || undefined,
      nameKanaLike: queryParams.nameKanaLike || undefined,
      phoneLike: queryParams.phoneLike || undefined,
      startDateFrom: queryParams.startDateFrom || undefined,
      startDateTo: queryParams.startDateTo || undefined,
      endDateFrom: queryParams.endDateFrom || undefined,
      endDateTo: queryParams.endDateTo || undefined,
      isEchoesLinked: normalizeEchoesLinkedFilter(queryParams.isEchoesLinked),
    } as SearchFormValues
  }, [queryParams])

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      id: queryParams.id || undefined,
      propertyNameLike: queryParams.propertyNameLike || "",
      roomNameLike: queryParams.roomNameLike || "",
      propertyAddressLike: queryParams.propertyAddressLike || "",
      nameLike: queryParams.nameLike || "",
      nameKanaLike: queryParams.nameKanaLike || "",
      phoneLike: queryParams.phoneLike || "",
      startDateFrom: parseDate(queryParams.startDateFrom) || undefined,
      startDateTo: parseDate(queryParams.startDateTo) || undefined,
      endDateFrom: parseDate(queryParams.endDateFrom) || undefined,
      endDateTo: parseDate(queryParams.endDateTo) || undefined,
      isEchoesLinked: normalizeEchoesLinkedFilter(queryParams.isEchoesLinked),
    },
  })

  const onSubmit = (formData: SearchFormValues) => {
    // URLパラメーターを更新
    setQueryParams({
      page: 1,
      id: formData.id || null,
      propertyNameLike: formData.propertyNameLike,
      roomNameLike: formData.roomNameLike,
      propertyAddressLike: formData.propertyAddressLike,
      nameLike: formData.nameLike,
      nameKanaLike: formData.nameKanaLike,
      phoneLike: formData.phoneLike,
      startDateFrom: formatDateForApi(formData.startDateFrom),
      startDateTo: formatDateForApi(formData.startDateTo),
      endDateFrom: formatDateForApi(formData.endDateFrom),
      endDateTo: formatDateForApi(formData.endDateTo),
      isEchoesLinked: formData.isEchoesLinked === "all" ? null : formData.isEchoesLinked || null,
    })
    queryClient.invalidateQueries({ queryKey: ["/tenants"] })
  }

  const onReset = () => {
    form.reset()

    // NOTE: IDがリセット時にvalueの値がリセットされないため
    // DOMを直接操作してIDフィールドをクリア
    if (idInputRef.current) {
      idInputRef.current.value = ""
    }

    // URLパラメーターをクリア
    setQueryParams({
      page: 1,
      id: null,
      propertyNameLike: null,
      roomNameLike: null,
      propertyAddressLike: null,
      nameLike: null,
      nameKanaLike: null,
      phoneLike: null,
      startDateFrom: null,
      startDateTo: null,
      endDateFrom: null,
      endDateTo: null,
      isEchoesLinked: null,
    })
  }

  const handlePageChange = (page: number) => {
    setQueryParams({ page })
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">賃借人一覧</h1>

      <div className="">
        <div className="p-4 border-b bg-white rounded-lg shadow-sm">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="propertyNameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>物件名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="roomNameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>部屋番号</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="propertyAddressLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>物件住所</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="nameLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>賃借人名</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="nameKanaLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>賃借人名（カナ）</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phoneLike"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>電話番号</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder=""
                          inputMode="numeric"
                          pattern="[0-9]*"
                          {...field}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "")
                            field.onChange(value)
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>賃借人ID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder=""
                          {...field}
                          ref={idInputRef}
                          value={field.value ?? undefined}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? Number.parseInt(e.target.value, 10) : null
                            )
                          }
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="font-bold">契約開始日</Label>
                  <div className="flex items-start gap-2">
                    <FormField
                      control={form.control}
                      name="startDateFrom"
                      render={({ field }) => (
                        <FormItem className="min-w-0 flex-1">
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="From"
                              className="h-[32px]"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <span className="h-8 content-center text-slate-500">〜</span>
                    <FormField
                      control={form.control}
                      name="startDateTo"
                      render={({ field }) => (
                        <FormItem className="min-w-0 flex-1">
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="To"
                              className="h-[32px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-bold">契約終了日</Label>
                  <div className="flex items-start gap-2">
                    <FormField
                      control={form.control}
                      name="endDateFrom"
                      render={({ field }) => (
                        <FormItem className="min-w-0 flex-1">
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="From"
                              className="h-[32px]"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <span className="h-8 content-center text-slate-500">〜</span>
                    <FormField
                      control={form.control}
                      name="endDateTo"
                      render={({ field }) => (
                        <FormItem className="min-w-0 flex-1">
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="To"
                              className="h-[32px]"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="isEchoesLinked"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={"font-bold"}>Echoes連携</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          options={echoesLinkedFilterOptions}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" variant="default" size={"sm"}>
                  検索
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className={"font-bold border-primary"}
                  size={"sm"}
                  onClick={onReset}
                >
                  検索をリセット
                </Button>
              </div>
            </form>
          </Form>
        </div>

        <ReactErrorBoundary>
          <Suspense fallback={<OwnerPageFallback />}>
            <OwnerPageContent
              searchParams={searchParams}
              currentPage={queryParams.page}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
            />
          </Suspense>
        </ReactErrorBoundary>
      </div>
    </div>
  )
}

interface PageContentProps {
  searchParams: SearchFormValues | null
  currentPage: number
  onPageChange: (page: number) => void
  itemsPerPage: number
}

const OwnerPageContent = ({
  searchParams,
  currentPage,
  onPageChange,
  itemsPerPage,
}: PageContentProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const handleEdit = (id: number) => {
    navigate(`/tenants/${id}/edit`)
  }

  const handleDetail = (id: number) => {
    openInNewTab(`/tenants/${id}`)
  }

  const apiParams = useMemo(() => {
    const params: TenantModels.TenantListParams = {
      page: currentPage,
      limit: itemsPerPage,
    }

    type SearchableKeys = keyof TenantModels.TenantListParams
    const keys: readonly SearchableKeys[] = [
      "id",
      "propertyNameLike",
      "nameLike",
      "nameKanaLike",
      "propertyAddressLike",
      "roomNameLike",
      "phoneLike",
      "startDateFrom",
      "startDateTo",
      "endDateFrom",
      "endDateTo",
    ] as const

    keys.forEach((key) => {
      if (searchParams?.[key as keyof SearchFormValues]) {
        params[key as keyof TenantModels.TenantListParams] = searchParams[
          key as keyof SearchFormValues
          // biome-ignore lint/suspicious/noExplicitAny: 暫定対応
        ] as any
      }
    })

    if (searchParams?.isEchoesLinked === "true") {
      params.isEchoesLinked = TenantListIsEchoesLinked.true
    } else if (searchParams?.isEchoesLinked === "false") {
      params.isEchoesLinked = TenantListIsEchoesLinked.false
    }

    params.withRoomProperty = "true"
    params.withRoom = "true"
    params.withPlans = "true"
    return params
  }, [searchParams, currentPage, itemsPerPage])

  // APIリクエストパラメータを構築
  const { data, refetch } = tenantApi.useTenantListSuspense(apiParams)

  // ページに戻ってきた時にデータを再取得
  useEffect(() => {
    const state = location.state as { refetch?: boolean } | null
    if (state?.refetch) {
      refetch()
      // location.stateをクリアして、次回のナビゲーションで問題が起きないようにする
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, refetch, navigate, location.pathname])

  // ページネーション計算
  const totalCount = data.total
  const totalPages = Math.ceil(totalCount / itemsPerPage)

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  const renderPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    const halfRange = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfRange)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={(e) => {
              e.preventDefault()
              handlePageChange(i)
            }}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      )
    }
    return pages
  }

  const renderAddress = (room?: TenantList200TenantsItemRoom) => {
    if (!room?.property) return "-"
    return `${room.property.prefecture}${room.property.city}${room.property.town}`
  }

  return (
    <div className="mt-4">
      <div className="flex items-end justify-between mb-4">
        <div className="text-sm text-slate-400">{totalCount}件の賃借人が見つかりました</div>
        <div>
          <Link to="/tenants/add">
            <Button size={"sm"}>
              <Plus className="mr-2" size={16} />
              新規賃借人追加
            </Button>
          </Link>
        </div>
      </div>

      <div className="bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap font-bold">賃借人ID</TableHead>
              <TableHead className="whitespace-nowrap font-bold min-w-[250px]">賃借人名</TableHead>
              <TableHead className="whitespace-nowrap font-bold">賃借人名（カナ）</TableHead>
              <TableHead className="whitespace-nowrap font-bold">物件名</TableHead>
              <TableHead className="whitespace-nowrap font-bold">部屋番号</TableHead>
              <TableHead className="whitespace-nowrap font-bold">物件住所</TableHead>
              <TableHead className="whitespace-nowrap font-bold">電話番号</TableHead>
              <TableHead className="whitespace-nowrap font-bold">初回契約開始日</TableHead>
              <TableHead className="whitespace-nowrap font-bold">契約終了日</TableHead>
              <TableHead className="whitespace-nowrap font-bold">Echoes連携</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.tenants.map((tenant) => {
              const {
                label: planLabel,
                variant: planVariant,
                suspended: planSuspended,
              } = getTenantRoomPlanInfo(tenant)
              const isUnusedPlan = planVariant === "nonePlan"
              const propertyName = tenant.room?.property?.name
              const roomName = tenant.room?.name

              return (
                <TableRow
                  key={tenant.id}
                  className={cn(
                    isUnusedPlan && "bg-slate-200/70 text-slate-600 hover:bg-slate-200"
                  )}
                >
                  <TableCell>{tenant.id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Link
                        className={cn(
                          "text-blue-600 cursor-pointer hover:underline",
                          isUnusedPlan && "text-slate-500"
                        )}
                        to={`/tenants/${tenant.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {tenant.name}
                      </Link>
                      <Label
                        variant={planVariant}
                        className={cn(isUnusedPlan && "bg-slate-300 text-slate-600")}
                      >
                        {planLabel}
                      </Label>
                      {planSuspended && <Label variant="planSuspended">停止中</Label>}
                    </div>
                  </TableCell>
                  <TableCell>{tenant.nameKana || "-"}</TableCell>
                  <TableCell>{propertyName || "-"}</TableCell>
                  <TableCell>{roomName || "-"}</TableCell>
                  <TableCell>{renderAddress(tenant.room)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 whitespace-nowrap">
                      {getTenantPhones(tenant).map((phone) => (
                        <span key={phone}>{phone}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{tenant.startDate}</TableCell>
                  <TableCell>{tenant.endDate}</TableCell>
                  <TableCell>
                    <EchoesSyncCell
                      echoesId={tenant.echoesTenantId}
                      echoesFailedAt={tenant.echoesFailedAt}
                    />
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[200px]">
                        <DropdownMenuItem onClick={() => handleDetail(tenant.id)}>
                          <Book className="text-slate-600 mr-2" size={16} />
                          詳細
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(tenant.id)}>
                          <Edit className="text-slate-600 mr-2" size={16} />
                          編集
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            openInNewTab(createTicketSearchUrl({ tenantName: tenant.name }))
                          }
                        >
                          <FileText className="text-slate-600 mr-2" size={16} />
                          賃借人の案件
                        </DropdownMenuItem>
                        {propertyName && (
                          <DropdownMenuItem
                            onClick={() => openInNewTab(createTicketSearchUrl({ propertyName }))}
                          >
                            <Home className="text-slate-600 mr-2" size={16} />
                            物件の案件
                          </DropdownMenuItem>
                        )}
                        {propertyName && roomName && (
                          <DropdownMenuItem
                            onClick={() =>
                              openInNewTab(createTicketSearchUrl({ propertyName, roomName }))
                            }
                          >
                            <Book className="text-slate-600 mr-2" size={16} />
                            部屋の案件
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )
            })}
            {data.total === 0 && (
              <TableRow>
                <TableCell colSpan={12} className="text-center py-8">
                  賃借人が見つかりませんでした
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-col">
          <div className="mt-4 flex items-center justify-between ml-auto">
            <div className="text-sm flex-shrink-0 text-primary mr-[30px]">
              {totalCount} 件中 {(currentPage - 1) * itemsPerPage + 1} -{" "}
              {Math.min(currentPage * itemsPerPage, totalCount)} 件を表示
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationFirst
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(1)
                    }}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(currentPage - 1)
                    }}
                    disabled={currentPage === 1}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                {renderPageNumbers()}
                <PaginationItem>
                  <PaginationNext
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(currentPage + 1)
                    }}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLast
                    onClick={(e) => {
                      e.preventDefault()
                      handlePageChange(totalPages)
                    }}
                    disabled={currentPage === totalPages}
                    className="cursor-pointer"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  )
}
function OwnerPageFallback() {
  return (
    <div className="p-10 flex flex-col items-center justify-center">
      <Spinner className={"text-slate-600 size-10 transition-opacity"} />
    </div>
  )
}
